import React, {useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {PoiBusIcon, PoiEducationIcon, PoiHealthIcon, PoiShopIcon, PoiSportIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {pluralize} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {useTransportPois} from "../../../maps/hooks/use_transport_pois";
import {IPoi} from "../../../maps/types/IPoi";
import {getClosestTransportPoi} from "../../../maps/utils/get_closest_transport_poi";
import {PoiType, TransportPoiType} from "../../../maps/utils/PoiType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {PoiStatsItem} from "../actions/fetch_offer_detail_poi";
import {SchemaOfferDetailFAQ} from "../schema/SchemaOfferDetailFAQ";
import {IOfferPoiDescription} from "../types/IOfferPoiDescription";

interface IProps {
    className?: string;
    offer: IOfferDetail;
    geoAreaCoordinates: number[][][];
    geoPointCoordinates: [number, number];
    property?: {
        id: number;
        for_sale: boolean;
    };
    changeModalTab: (tab: OfferModalActiveTab.MAP) => void;
    pois: Record<PoiType, IPoi[] | Record<TransportPoiType, IPoi[]>>;
}

const TEXT_MARGIN_BOTTOM = 1;

export const OfferDetailLocation = (props: IProps) => {
    const {offer, pois} = props;
    const transportPoisFromApi = (pois?.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const isNearbyPluralize = pluralize(["znajduje się", "znajdują się", "znajduje się"]);
    const educationPlacesPluralize = pluralize(["placówka edukacyjna", "placówki edukacyjne", "placówek edukacyjnych"]);

    const {transportPois} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });

    const transportPoi = useMemo(() => getClosestTransportPoi(transportPois), [transportPois]);

    const poisStats = useSelector((state: IRPStore) => state.offerDetail.poisStats?.poi?.nearest_pois_count);

    const hasEducationPois = !!poisStats?.education && poisStats.education.items?.length > 0;
    const hasHealthPois = !!poisStats?.health && poisStats.health.items?.length > 0;
    const hasShopPois = !!poisStats?.shops && poisStats.shops.items?.length > 0;
    const hasSportPois = !!poisStats?.sport && poisStats.sport.items?.length > 0;

    const poiDescriptionMap = offer.poi_description.reduce(
        (acc, item) => {
            return {...acc, [item.category_slug]: item};
        },
        {} as Record<string, IOfferPoiDescription>
    );

    const generateCategoryDescription = (category: PoiStatsItem[], preText?: string, postText?: string, distanceFirst?: boolean) => {
        const elem = (item: number): {text: string; grouped: boolean} => {
            if (!category[item]) {
                return {text: "", grouped: false};
            }

            const distance = Math.max(50, Math.round(category[item]?.distance / 100) * 100);
            const name = category[item]?.name;
            const isGrouped = category.some((cat, index) => index !== item && cat?.type === category[item]?.type);

            return {
                text: distanceFirst
                    ? `${preText ? `${preText} ` : ""}${distance} m${postText ? ` ${postText} ` : " "}${name}`
                    : `${preText ? `${preText} ` : ""}${name}${postText ? ` ${postText} ` : " "}${distance} m`,
                grouped: isGrouped
            };
        };

        const groupedIndexes = category.map((_, index) => (elem(index).grouped ? index : -1)).filter((index) => index !== -1);

        const hasGrouped = groupedIndexes.length > 0;

        return hasGrouped
            ? `${elem(0).text + (groupedIndexes.length === 3 ? "." : ", ")}${groupedIndexes.length === 2 ? (groupedIndexes[0] === 0 && groupedIndexes[1] === 2 ? elem(1).text : elem(2).text) + "." : ""}`
            : `${category.length > 0 ? elem(0).text + (category.length > 1 ? ", " : ".") : ""}${
                  category.length > 1 ? elem(1).text + (category.length > 2 ? ", " : ".") : ""
              }${category.length > 2 ? elem(2).text + "." : ""}`;
    };

    const transportGeneratedDescription =
        !!transportPoi &&
        (!poiDescriptionMap.transport || (poiDescriptionMap.transport && !poiDescriptionMap.transport.hide_auto_description)) &&
        `Przyszli mieszkańcy inwestycji ${offer.name} mogą korzystać z rozbudowanej sieci połączeń komunikacyjnych. W odległości ${Math.max(
            50,
            Math.round(transportPoi.distance / 100) * 100
        )} metrów od inwestycji ${offer.name} znajduje się najbliższy punkt komunikacyjny - ${transportPoi.name}.`;

    const transportSubHeading = `Jak wygląda komunikacja w okolicy inwestycji ${offer.name}?`;

    const educationGeneratedDescription =
        hasEducationPois &&
        (!poiDescriptionMap.education || (poiDescriptionMap.education && !poiDescriptionMap.education.hide_auto_description)) &&
        `Mieszkańcy mogą korzystać z bogatej oferty edukacyjnej. W najbliższej okolicy inwestycji ${offer.name} ${isNearbyPluralize(poisStats?.education.count)} ${
            poisStats?.education.count
        } ${educationPlacesPluralize(poisStats?.education.count)}. Najbliżej położone to: ${generateCategoryDescription(poisStats?.education.items, "", "w odległości")}`;

    const educationSubHeading = `Czy w pobliżu inwestycji ${offer.name} znajdują się szkoły/przedszkola?`;

    const servicesGeneratedDescription =
        hasShopPois &&
        (!poiDescriptionMap.services || (poiDescriptionMap.services && !poiDescriptionMap.services.hide_auto_description)) &&
        `Na infrastrukturę handlowo-usługową inwestycji ${offer.name} składa się wiele punktów. Nieopodal, ${generateCategoryDescription(
            poisStats?.shops.items,
            "w odległości",
            "znajduje się",
            true
        )}`;

    const servicesSubHeading = "Jak daleko jest do najbliższego sklepu?";

    const prepareDescription = (text: string) => {
        return (
            <>
                {text.split("\n").map((item, key) => (
                    <Text key={key} variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                        {item.trim()}
                    </Text>
                ))}
            </>
        );
    };

    return (
        <div>
            <Text variant="headline_4" as="h2" className={headerStyles}>
                Lokalizacja inwestycji
            </Text>

            {(offer.location_description || offer.region.included_location?.description) && (
                <div className={locationDescriptionTextStyle}>
                    {offer.location_description && prepareDescription(offer.location_description)}
                    {offer.region.included_location?.description && prepareDescription(offer.region.included_location?.description)}
                </div>
            )}

            {(offer.region.included_location?.transport_intro || poiDescriptionMap.transport) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <div className={locationItemIconStyle}>
                            <PoiBusIcon size="1.5" wrapperSize="2" wrapperColor="#9BD7FF" wrapperType="circle" />
                        </div>
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Komunikacja
                        </Text>
                    </div>
                    <div className={locationDescriptionBody}>
                        <Text variant="body_copy_1" as="h4" mb={TEXT_MARGIN_BOTTOM} className={locationItemSubTitleStyle}>
                            {transportSubHeading}
                        </Text>

                        {(!poiDescriptionMap.transport || (poiDescriptionMap.transport && !poiDescriptionMap.transport.hide_auto_description)) && (
                            <>
                                {offer.region.included_location?.transport_intro && (
                                    <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                        {offer.region.included_location?.transport_intro}
                                    </Text>
                                )}

                                {transportGeneratedDescription && (
                                    <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                        {transportGeneratedDescription}
                                    </Text>
                                )}
                            </>
                        )}

                        {poiDescriptionMap.transport && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.transport.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasEducationPois || poiDescriptionMap.education) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiEducationIcon size="1.5" wrapperSize="2" wrapperColor="#B9E19B" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Edukacja
                        </Text>
                    </div>
                    <div className={locationDescriptionBody}>
                        <Text variant="body_copy_1" as="h4" className={locationItemSubTitleStyle} mb={TEXT_MARGIN_BOTTOM}>
                            {educationSubHeading}
                        </Text>

                        {educationGeneratedDescription &&
                            (!poiDescriptionMap.education || (poiDescriptionMap.education && !poiDescriptionMap.education.hide_auto_description)) && (
                                <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                    {educationGeneratedDescription}
                                </Text>
                            )}

                        {poiDescriptionMap.education && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.education.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasHealthPois || poiDescriptionMap.medical_care) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiHealthIcon size="1.5" wrapperSize="2" wrapperColor="#D7AFE1" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Opieka medyczna
                        </Text>
                    </div>
                    <div className={locationDescriptionBody}>
                        {hasHealthPois &&
                            (!poiDescriptionMap.medical_care || (poiDescriptionMap.medical_care && !poiDescriptionMap.medical_care.hide_auto_description)) && (
                                <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                    Dużym atutem inwestycji {offer.name} jest bliskość ośrodków zdrowotnych. Dostęp do opieki medycznej zapewnią położone w
                                    pobliżu: {generateCategoryDescription(poisStats?.health.items, "", "w odległości")}
                                </Text>
                            )}

                        {poiDescriptionMap.medical_care && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.medical_care.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasShopPois || poiDescriptionMap.services) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiShopIcon size="1.5" wrapperSize="2" wrapperColor="#FFCDA5" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Sklepy i usługi
                        </Text>
                    </div>

                    <div className={locationDescriptionBody}>
                        <Text variant="body_copy_1" as="h4" className={locationItemSubTitleStyle} mb={TEXT_MARGIN_BOTTOM}>
                            {servicesSubHeading}
                        </Text>

                        {servicesGeneratedDescription &&
                            (!poiDescriptionMap.services || (poiDescriptionMap.services && !poiDescriptionMap.services.hide_auto_description)) && (
                                <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                    {servicesGeneratedDescription}
                                </Text>
                            )}

                        {poiDescriptionMap.services && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.services.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasSportPois || poiDescriptionMap.sport) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiSportIcon size="1.5" wrapperSize="2" wrapperColor="#E1D7CD" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Sport
                        </Text>
                    </div>

                    <div className={locationDescriptionBody}>
                        {hasSportPois && (!poiDescriptionMap.sport || (poiDescriptionMap.sport && !poiDescriptionMap.sport.hide_auto_description)) && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                Bliskość terenów rekreacyjnych oraz obiektów sportowych to mocna strona tej lokalizacji. W najbliższej okolicy inwestycji{" "}
                                {offer.name} {generateCategoryDescription(poisStats?.sport.items, "w odległości", "znajduje się", true)}
                            </Text>
                        )}

                        {poiDescriptionMap.sport && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.sport.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            <SchemaOfferDetailFAQ
                transportAnswer={transportGeneratedDescription}
                transportQuestion={transportSubHeading}
                educationAnswer={educationGeneratedDescription}
                educationQuestion={educationSubHeading}
                servicesAnswer={servicesGeneratedDescription}
                servicesQuestion={servicesSubHeading}
            />
        </div>
    );
};

const locationDescriptionTextStyle = css({
    mb: 2,

    sm: {
        mb: 3
    }
});

const headerStyles = css({
    pb: 2,

    sm: {
        pb: 3
    }
});

const locationDescriptionBody = css({
    pb: "2.8rem",

    sm: {
        pb: "2rem"
    }
});

const locationItemHeadingStyle = css({
    pb: 3,
    display: "flex",
    alignItems: "center"
});

const locationItemIconStyle = css({
    p: 0
});

const locationItemTitleStyle = css({
    p: "0 0 0 1.6rem"
});

const locationItemSubTitleStyle = css({
    p: 0,
    fontWeight: "600"
});
import React, {useMemo} from "react";
import {useSelector} from "react-redux";
import {css} from "@panda/css";

import {PoiBusIcon, PoiEducationIcon, PoiHealthIcon, PoiShopIcon, PoiSportIcon} from "@pg-design/icons-module";
import {Text} from "@pg-design/text-module";
import {pluralize} from "@pg-mono/string-utils";

import {IRPStore} from "../../../app/rp_reducer";
import {useTransportPois} from "../../../maps/hooks/use_transport_pois";
import {IPoi} from "../../../maps/types/IPoi";
import {getClosestTransportPoi} from "../../../maps/utils/get_closest_transport_poi";
import {PoiType, TransportPoiType} from "../../../maps/utils/PoiType";
import {IOfferDetail} from "../../types/IOfferDetail";
import {OfferModalActiveTab} from "../../types/OfferModalActiveTab";
import {PoiStatsItem} from "../actions/fetch_offer_detail_poi";
import {SchemaOfferDetailFAQ} from "../schema/SchemaOfferDetailFAQ";
import {IOfferPoiDescription} from "../types/IOfferPoiDescription";

interface IProps {
    className?: string;
    offer: IOfferDetail;
    geoAreaCoordinates: number[][][];
    geoPointCoordinates: [number, number];
    property?: {
        id: number;
        for_sale: boolean;
    };
    changeModalTab: (tab: OfferModalActiveTab.MAP) => void;
    pois: Record<PoiType, IPoi[] | Record<TransportPoiType, IPoi[]>>;
}

const TEXT_MARGIN_BOTTOM = 1;

export const OfferDetailLocation = (props: IProps) => {
    const {offer, pois} = props;
    const transportPoisFromApi = (pois?.transport || {}) as Record<TransportPoiType, IPoi[]>;

    const isNearbyPluralize = pluralize(["znajduje się", "znajdują się", "znajduje się"]);
    const educationPlacesPluralize = pluralize(["placówka edukacyjna", "placówki edukacyjne", "placówek edukacyjnych"]);

    const {transportPois} = useTransportPois(transportPoisFromApi, {
        longitude: offer.geo_point.coordinates[0],
        latitude: offer.geo_point.coordinates[1],
        radius: 3000
    });

    const transportPoi = useMemo(() => getClosestTransportPoi(transportPois), [transportPois]);

    const poisStats = useSelector((state: IRPStore) => state.offerDetail.poisStats?.poi?.nearest_pois_count);

    const hasEducationPois = !!poisStats?.education && poisStats.education.items?.length > 0;
    const hasHealthPois = !!poisStats?.health && poisStats.health.items?.length > 0;
    const hasShopPois = !!poisStats?.shops && poisStats.shops.items?.length > 0;
    const hasSportPois = !!poisStats?.sport && poisStats.sport.items?.length > 0;

    const poiDescriptionMap = offer.poi_description.reduce(
        (acc, item) => {
            return {...acc, [item.category_slug]: item};
        },
        {} as Record<string, IOfferPoiDescription>
    );

    const generateCategoryDescription = (category: PoiStatsItem[], preText?: string, postText?: string, distanceFirst?: boolean) => {
        const elem = (item: number): {text: string; grouped: boolean} => {
            if (!category[item]) {
                return {text: "", grouped: false};
            }

            const distance = Math.max(50, Math.round(category[item]?.distance / 100) * 100);
            const name = category[item]?.name;
            const isGrouped = category.some((cat, index) => index !== item && cat?.type === category[item]?.type);

            return {
                text: distanceFirst
                    ? `${preText ? `${preText} ` : ""}${distance} m${postText ? ` ${postText} ` : " "}${name}`
                    : `${preText ? `${preText} ` : ""}${name}${postText ? ` ${postText} ` : " "}${distance} m`,
                grouped: isGrouped
            };
        };

        const groupedIndexes = category.map((_, index) => (elem(index).grouped ? index : -1)).filter((index) => index !== -1);

        const hasGrouped = groupedIndexes.length > 0;

        return hasGrouped
            ? `${elem(0).text + (groupedIndexes.length === 3 ? "." : ", ")}${groupedIndexes.length === 2 ? (groupedIndexes[0] === 0 && groupedIndexes[1] === 2 ? elem(1).text : elem(2).text) + "." : ""}`
            : `${category.length > 0 ? elem(0).text + (category.length > 1 ? ", " : ".") : ""}${
                  category.length > 1 ? elem(1).text + (category.length > 2 ? ", " : ".") : ""
              }${category.length > 2 ? elem(2).text + "." : ""}`;
    };

    const transportGeneratedDescription =
        !!transportPoi &&
        (!poiDescriptionMap.transport || (poiDescriptionMap.transport && !poiDescriptionMap.transport.hide_auto_description)) &&
        `Przyszli mieszkańcy inwestycji ${offer.name} mogą korzystać z rozbudowanej sieci połączeń komunikacyjnych. W odległości ${Math.max(
            50,
            Math.round(transportPoi.distance / 100) * 100
        )} metrów od inwestycji ${offer.name} znajduje się najbliższy punkt komunikacyjny - ${transportPoi.name}.`;

    const transportSubHeading = `Jak wygląda komunikacja w okolicy inwestycji ${offer.name}?`;

    const educationGeneratedDescription =
        hasEducationPois &&
        (!poiDescriptionMap.education || (poiDescriptionMap.education && !poiDescriptionMap.education.hide_auto_description)) &&
        `Mieszkańcy mogą korzystać z bogatej oferty edukacyjnej. W najbliższej okolicy inwestycji ${offer.name} ${isNearbyPluralize(poisStats?.education.count)} ${
            poisStats?.education.count
        } ${educationPlacesPluralize(poisStats?.education.count)}. Najbliżej położone to: ${generateCategoryDescription(poisStats?.education.items, "", "w odległości")}`;

    const educationSubHeading = `Czy w pobliżu inwestycji ${offer.name} znajdują się szkoły/przedszkola?`;

    const servicesGeneratedDescription =
        hasShopPois &&
        (!poiDescriptionMap.services || (poiDescriptionMap.services && !poiDescriptionMap.services.hide_auto_description)) &&
        `Na infrastrukturę handlowo-usługową inwestycji ${offer.name} składa się wiele punktów. Nieopodal, ${generateCategoryDescription(
            poisStats?.shops.items,
            "w odległości",
            "znajduje się",
            true
        )}`;

    const servicesSubHeading = "Jak daleko jest do najbliższego sklepu?";

    const prepareDescription = (text: string) => {
        return (
            <>
                {text.split("\n").map((item, key) => (
                    <Text key={key} variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                        {item.trim()}
                    </Text>
                ))}
            </>
        );
    };

    return (
        <div>
            <Text variant="headline_4" as="h2" className={headerStyles}>
                Lokalizacja inwestycji
            </Text>

            {(offer.location_description || offer.region.included_location?.description) && (
                <div className={locationDescriptionTextStyle}>
                    {offer.location_description && prepareDescription(offer.location_description)}
                    {offer.region.included_location?.description && prepareDescription(offer.region.included_location?.description)}
                </div>
            )}

            {(offer.region.included_location?.transport_intro || poiDescriptionMap.transport) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <div className={locationItemIconStyle}>
                            <PoiBusIcon size="1.5" wrapperSize="2" wrapperColor="#9BD7FF" wrapperType="circle" />
                        </div>
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Komunikacja
                        </Text>
                    </div>
                    <div className={locationDescriptionBody}>
                        <Text variant="body_copy_1" as="h4" mb={TEXT_MARGIN_BOTTOM} className={locationItemSubTitleStyle}>
                            {transportSubHeading}
                        </Text>

                        {(!poiDescriptionMap.transport || (poiDescriptionMap.transport && !poiDescriptionMap.transport.hide_auto_description)) && (
                            <>
                                {offer.region.included_location?.transport_intro && (
                                    <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                        {offer.region.included_location?.transport_intro}
                                    </Text>
                                )}

                                {transportGeneratedDescription && (
                                    <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                        {transportGeneratedDescription}
                                    </Text>
                                )}
                            </>
                        )}

                        {poiDescriptionMap.transport && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.transport.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasEducationPois || poiDescriptionMap.education) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiEducationIcon size="1.5" wrapperSize="2" wrapperColor="#B9E19B" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Edukacja
                        </Text>
                    </div>
                    <div className={locationDescriptionBody}>
                        <Text variant="body_copy_1" as="h4" className={locationItemSubTitleStyle} mb={TEXT_MARGIN_BOTTOM}>
                            {educationSubHeading}
                        </Text>

                        {educationGeneratedDescription &&
                            (!poiDescriptionMap.education || (poiDescriptionMap.education && !poiDescriptionMap.education.hide_auto_description)) && (
                                <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                    {educationGeneratedDescription}
                                </Text>
                            )}

                        {poiDescriptionMap.education && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.education.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasHealthPois || poiDescriptionMap.medical_care) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiHealthIcon size="1.5" wrapperSize="2" wrapperColor="#D7AFE1" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Opieka medyczna
                        </Text>
                    </div>
                    <div className={locationDescriptionBody}>
                        {hasHealthPois &&
                            (!poiDescriptionMap.medical_care || (poiDescriptionMap.medical_care && !poiDescriptionMap.medical_care.hide_auto_description)) && (
                                <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                    Dużym atutem inwestycji {offer.name} jest bliskość ośrodków zdrowotnych. Dostęp do opieki medycznej zapewnią położone w
                                    pobliżu: {generateCategoryDescription(poisStats?.health.items, "", "w odległości")}
                                </Text>
                            )}

                        {poiDescriptionMap.medical_care && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.medical_care.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasShopPois || poiDescriptionMap.services) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiShopIcon size="1.5" wrapperSize="2" wrapperColor="#FFCDA5" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Sklepy i usługi
                        </Text>
                    </div>

                    <div className={locationDescriptionBody}>
                        <Text variant="body_copy_1" as="h4" className={locationItemSubTitleStyle} mb={TEXT_MARGIN_BOTTOM}>
                            {servicesSubHeading}
                        </Text>

                        {servicesGeneratedDescription &&
                            (!poiDescriptionMap.services || (poiDescriptionMap.services && !poiDescriptionMap.services.hide_auto_description)) && (
                                <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                    {servicesGeneratedDescription}
                                </Text>
                            )}

                        {poiDescriptionMap.services && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.services.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            {(hasSportPois || poiDescriptionMap.sport) && (
                <>
                    <div className={locationItemHeadingStyle}>
                        <PoiSportIcon size="1.5" wrapperSize="2" wrapperColor="#E1D7CD" wrapperType="circle" />
                        <Text variant="headline_6" as="h3" className={locationItemTitleStyle}>
                            Sport
                        </Text>
                    </div>

                    <div className={locationDescriptionBody}>
                        {hasSportPois && (!poiDescriptionMap.sport || (poiDescriptionMap.sport && !poiDescriptionMap.sport.hide_auto_description)) && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                Bliskość terenów rekreacyjnych oraz obiektów sportowych to mocna strona tej lokalizacji. W najbliższej okolicy inwestycji{" "}
                                {offer.name} {generateCategoryDescription(poisStats?.sport.items, "w odległości", "znajduje się", true)}
                            </Text>
                        )}

                        {poiDescriptionMap.sport && (
                            <Text variant="body_copy_1" mb={TEXT_MARGIN_BOTTOM}>
                                {poiDescriptionMap.sport.description}
                            </Text>
                        )}
                    </div>
                </>
            )}

            <SchemaOfferDetailFAQ
                transportAnswer={transportGeneratedDescription}
                transportQuestion={transportSubHeading}
                educationAnswer={educationGeneratedDescription}
                educationQuestion={educationSubHeading}
                servicesAnswer={servicesGeneratedDescription}
                servicesQuestion={servicesSubHeading}
            />
        </div>
    );
};

const locationDescriptionTextStyle = css({
    mb: 2,

    sm: {
        mb: 3
    }
});

const headerStyles = css({
    pb: 2,

    sm: {
        pb: 3
    }
});

const locationDescriptionBody = css({
    pb: "2.8rem",

    sm: {
        pb: "2rem"
    }
});

const locationItemHeadingStyle = css({
    pb: 3,
    display: "flex",
    alignItems: "center"
});

const locationItemIconStyle = css({
    p: 0
});

const locationItemTitleStyle = css({
    p: "0 0 0 1.6rem"
});

const locationItemSubTitleStyle = css({
    p: 0,
    fontWeight: "600"
});
